// ===================================
// TODO CLEAN - FOOTER COMPONENT
// Site footer with links, contact info, and legal pages
// ===================================

import React from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG, NAVIGATION_ROUTES } from '@/utils/constants';
import { WhatsAppButton, PhoneButton } from '@/components/ui/Button';
import logoImage from '@/assets/images/logo.png';
import { InstagramIcon, GoogleIcon } from '@/components/icons/SocialIcons';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

// ==========================================
// FOOTER COMPONENT
// ==========================================

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-900 text-neutral-300" role="contentinfo">
      <div className="w-full px-5 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-10 sm:py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-4 mb-6">
                {/* Logo */}
                <img
                  src={logoImage}
                  alt="Todo Clean - Servicio de Limpieza Profesional"
                  className="h-12 sm:h-14 lg:h-16 w-auto flex-shrink-0"
                />
                <div>
                  <div className="text-base sm:text-lg font-semibold text-white mb-1">
                    Todo Clean Chillán
                  </div>
                  <div className="text-xs sm:text-sm text-primary-400">
                    Servicios Profesionales de Limpieza
                  </div>
                </div>
              </div>

              <p className="text-sm sm:text-base text-neutral-400 mb-6 max-w-md">
                Transformamos tu hogar con estándares americanos de limpieza.
                Puntualidad garantizada, 100% de satisfacción y el mejor servicio
                en la región del Ñuble.
              </p>

              {/* Contact buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <WhatsAppButton
                  size="sm"
                  message="¡Hola! Me interesa conocer más sobre sus servicios de limpieza."
                  trackingSource="footer"
                >
                  WhatsApp
                </WhatsAppButton>
                <PhoneButton
                  size="sm"
                  trackingSource="footer"
                >
                  {SITE_CONFIG.phone}
                </PhoneButton>
              </div>

            </div>

            {/* Navigation Links */}
            <div>
              <h4 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Navegación</h4>
              <ul className="space-y-2">
                {NAVIGATION_ROUTES.slice(0, 6).map((route) => (
                  <li key={route.path}>
                    <Link
                      to={route.path}
                      className="text-sm sm:text-base text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none focus:underline"
                      onClick={() => {
                        // Scroll to top after navigation
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      {route.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services & Business Hours */}
            <div>
              <h4 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Servicios</h4>
              <ul className="space-y-2 mb-6">
                <li>
                  <a
                    href="/servicios#residencial"
                    className="text-sm sm:text-base text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none focus:underline"
                  >
                    Servicios Residenciales
                  </a>
                </li>
                <li>
                  <a
                    href="/servicios#empresariales"
                    className="text-sm sm:text-base text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none focus:underline"
                  >
                    Servicios Empresariales
                  </a>
                </li>
                <li>
                  <a
                    href="/servicios#airbnb"
                    className="text-sm sm:text-base text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none focus:underline"
                  >
                    Servicios Airbnb
                  </a>
                </li>
                <li>
                  <a
                    href="/servicios#eventos"
                    className="text-sm sm:text-base text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none focus:underline"
                  >
                    Servicios para Eventos
                  </a>
                </li>
              </ul>

              {/* Business hours */}
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-white mb-2">Horarios de Atención</h4>
                <p className="text-xs sm:text-sm text-neutral-400">
                  Lunes a Sábado: {SITE_CONFIG.businessHours?.monday?.open || '9:00'} - {SITE_CONFIG.businessHours?.saturday?.close || '18:00'}
                </p>
                <p className="text-xs sm:text-sm text-neutral-400">
                  Domingo: {SITE_CONFIG.businessHours?.sunday?.open || '10:00'} - {SITE_CONFIG.businessHours?.sunday?.close || '16:00'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-2 pb-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
            {/* Copyright */}
            <div className="text-xs sm:text-sm text-neutral-400">
              <p>
                © {currentYear} Todo Clean Chillán. Todos los derechos reservados.
              </p>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-3 sm:gap-4">
              {SITE_CONFIG.social.instagram && (
                <a
                  href={SITE_CONFIG.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none"
                  aria-label="Síguenos en Instagram"
                >
                  <InstagramIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              )}

              {SITE_CONFIG.social.google && (
                <a
                  href={SITE_CONFIG.social.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-white transition-colors duration-200 focus:text-white focus:outline-none"
                  aria-label="Visita nuestro perfil de Google"
                >
                  <GoogleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Developer credits */}
        <div className="py-3 sm:py-4 border-t border-neutral-800/50 text-center">
          <p className="text-xs text-neutral-500 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2">
            <span className="flex items-center gap-1.5">
              <span>Desarrollado por</span>
              <a
                href="https://www.lemonsoftware.cl/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-neutral-400 hover:text-white transition-colors duration-200"
              >
                <img src="/lemon.png" alt="Lemon Software" className="w-4 h-4" />
                <span>Lemon Software Chile SpA</span>
              </a>
            </span>
            <a
              href="mailto:contacto@lemonsoftware.cl"
              className="inline-flex items-center gap-1 text-neutral-400 hover:text-white transition-colors duration-200"
            >
              <EnvelopeIcon className="w-3.5 h-3.5" />
              <span>contacto@lemonsoftware.cl</span>
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;